import React, { useState, useMemo, useEffect } from 'react';
import styles from './TransportationServiceSelector.module.scss';
import GenericSelect, { GenericSelectOption } from '../GenericSelect/GenericSelect';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import clsx from 'clsx';
import { TransportationType } from '../TransportationTypeSelector/TransportationTypeSelector';
import { TransportationService } from 'model/Service';
import { Spinner } from 'components/UI';
import { faExclamationTriangle } from '@fortawesome/pro-regular-svg-icons';
import { AlertNewBooking } from 'components/CreateBooking/UI/AlertNewBooking';

interface Props {
    loading?: boolean;
    value?: TransportationService;
    transportType: TransportationType;
    onChange?: (value: TransportationService) => void;
    className?: string;
}

export const getTransportationPrice = (service: TransportationService, transportType: TransportationType) => {
    switch (transportType) {
        case 'pickup':
            return `$${service.pickupPrice}`;
        case 'dropoff':
            return `$${service.dropoffPrice}`;
        default:
            return `$${service.pickupPrice + service.dropoffPrice}`;
    }
};

export const TransportationServiceSelector: React.FC<Props> = ({
    transportType,
    value,
    onChange,
    className,
    loading
}) => {
    const services = useSelector<RootState, TransportationService[] | undefined>(
        state => state.services.transportationServices
    );

    const [selected, setSelected] = useState<TransportationService | undefined>(value);

    useEffect(() => {
        setSelected(value);
    }, [value]);

    const options: GenericSelectOption[] = useMemo(() => {
        if (!services) return [];

        return services.map(s => ({
            id: s.serviceId,
            label: s.serviceName,
            color: s.color,
            rightLabel: getTransportationPrice(s, transportType)
        }));
    }, [services, transportType]);

    const handleSelect = (opt: GenericSelectOption) => {
        const selectedService = services?.find((op: TransportationService) => op.serviceId === opt.id);
        setSelected(selectedService);
        onChange?.(selectedService!);
    };

    const selectedOption = options.find(o => o.id === selected?.serviceId) ?? null;

    const emptyServicesComponent = (
        <div id="booking_div_no_services_alert">
            <label className={styles.title}>Transportation Services</label>
            <AlertNewBooking
                className={styles.alert}
                bWidth="2px"
                icon={faExclamationTriangle}
                iconSize="lg"
                iconColor="#EAB464"
                bgColor="#F8F5F1"
                bdColor="#BCB8AE"
            >
                <p className={styles.alertText}>No services available for this pet</p>
            </AlertNewBooking>
        </div>
    );

    return (
        <div className={clsx(styles.container, className)}>
            {loading && <Spinner />}
            {!loading && (!services || services?.length === 0) && emptyServicesComponent}
            {!loading && services && services?.length > 0 && (
                <>
                    <label className={styles.title}>Transportation Services</label>
                    <GenericSelect
                        placeholder="Select Service"
                        selected={selectedOption}
                        options={options}
                        onSelect={handleSelect}
                        className={styles.select}
                    />
                </>
            )}
        </div>
    );
};

export default TransportationServiceSelector;
